import { ChangeDetectionStrategy, Component, computed, inject, OnInit } from '@angular/core';

import { AvContentContainerComponent } from '../../../../../Shared/Components/av-content-container/av-content-container.component';
import { AvRailComponent } from '../../../../../Shared/Components/av-rail/av-rail.component';
import { AvSkeletonComponent } from '../../../../../Shared/Components/av-skeleton/av-skeleton.component';
import { COMMON_IMPORTS } from '../../../../../Shared/common-imports';
import { createCountUpProgress } from '../../../../../Shared/Services/count-up-progress.util';
import { waitState } from '../../../../../Shared/Services/wait-state.util';
import { DemandsDateRangeChange } from '../../../../../Catalog/Interfaces/demands-filter-panel.interface';
import { ReportSubjectTab } from '../../../../Demand/Types/report-subject-tab.type';
import { MonitoringFacade } from '../../../Facades/monitoring.facade';
import { MonitoringValidatorRow } from '../../../Interfaces/monitoring-report.interface';
import {
  buildMonitoringDemandsLink,
  buildMonitoringValidatorLink,
} from '../../../Shared/monitoring-demands-link.util';
import { MonitoringRowLinkBuilder } from '../../../Types/monitoring-row-link-builder.type';
import { MonitoringApprovalCardComponent } from '../../Partials/monitoring-approval-card/monitoring-approval-card.component';
import { MonitoringCatalogCardComponent } from '../../Partials/monitoring-catalog-card/monitoring-catalog-card.component';
import { MonitoringDurationCardComponent } from '../../Partials/monitoring-duration-card/monitoring-duration-card.component';
import { MonitoringKindComparisonComponent } from '../../Partials/monitoring-kind-comparison/monitoring-kind-comparison.component';
import { MonitoringStatusBreakdownComponent } from '../../Partials/monitoring-status-breakdown/monitoring-status-breakdown.component';
import { MonitoringToolbarComponent } from '../../Partials/monitoring-toolbar/monitoring-toolbar.component';
import { MonitoringTopTableComponent } from '../../Partials/monitoring-top-table/monitoring-top-table.component';
import { MonitoringVolumeCardComponent } from '../../Partials/monitoring-volume-card/monitoring-volume-card.component';

@Component({
  selector: 'app-monitoring',
  standalone: true,
  imports: [
    COMMON_IMPORTS,
    AvContentContainerComponent,
    AvRailComponent,
    AvSkeletonComponent,
    MonitoringToolbarComponent,
    MonitoringApprovalCardComponent,
    MonitoringCatalogCardComponent,
    MonitoringDurationCardComponent,
    MonitoringVolumeCardComponent,
    MonitoringKindComparisonComponent,
    MonitoringStatusBreakdownComponent,
    MonitoringTopTableComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './monitoring.component.html',
  styleUrl: './monitoring.component.scss',
})
export class MonitoringComponent implements OnInit {
  readonly facade = inject(MonitoringFacade);

  readonly coldWait = waitState(this.facade.coldLoading);

  readonly refreshWait = waitState(
    computed(() => this.facade.loading() && !this.facade.coldLoading()),
  );

  readonly showSkeleton = computed(() => this.facade.coldLoading() || this.coldWait.visible());

  readonly countUp = createCountUpProgress({
    ready: computed(() => this.facade.hasData() && !this.showSkeleton()),
  });

  readonly applicationRowLink = computed(() => this._rowLinkFor('application'));

  readonly groupRowLink = computed(() => this._rowLinkFor('group'));

  readonly validatorRowLink = computed(() => this._validatorRowLink());

  ngOnInit(): void {
    this.facade.initialise();
  }

  onRangeChange(range: DemandsDateRangeChange): void {
    this.facade.setRange(range);
  }

  private _rowLinkFor(tab: ReportSubjectTab): MonitoringRowLinkBuilder {
    const range = this._currentRange();
    return (row) => buildMonitoringDemandsLink(tab, row.id, range);
  }

  private _validatorRowLink(): MonitoringRowLinkBuilder<MonitoringValidatorRow> {
    const range = this._currentRange();
    return (row) => buildMonitoringValidatorLink(row, range);
  }

  private _currentRange(): DemandsDateRangeChange {
    return { from: this.facade.from(), to: this.facade.to() };
  }
}
